import {
  availableOverviewViews,
  overviewResources,
} from '@/modules/zy-crm/utils/overview-navigation';
import type { AuthorizedResourceAccessMap } from '@/permissions/adapter';
import {
  PERMISSION_ACTION_CODES,
  PERMISSION_RESOURCE_CODES,
} from '@/permissions/definitions';
import type { OverviewEntity } from '@/services/bff/crm-overview.api';

const buildAdminAccessMap = (
  authorizedResourceAccess: AuthorizedResourceAccessMap = {},
): AuthorizedResourceAccessMap => {
  const adminAccessMap: AuthorizedResourceAccessMap = {
    ...authorizedResourceAccess,
  };

  Object.values(PERMISSION_RESOURCE_CODES).forEach((resourceCode) => {
    adminAccessMap[resourceCode] = true;
  });

  Object.entries(PERMISSION_ACTION_CODES).forEach(
    ([resourceKey, resourceActions]) => {
      const resourceCode =
        PERMISSION_RESOURCE_CODES[
          resourceKey as keyof typeof PERMISSION_RESOURCE_CODES
        ];

      Object.values(resourceActions).forEach((actionCode) => {
        adminAccessMap[`${resourceCode}:${actionCode}`] = true;
      });
    },
  );

  return adminAccessMap;
};

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState:
    | {
        authorizedResourceAccess?: AuthorizedResourceAccessMap;
        currentTenantIsAdmin?: boolean;
      }
    | undefined,
) {
  const { authorizedResourceAccess, currentTenantIsAdmin } = initialState ?? {};

  const source = currentTenantIsAdmin
    ? buildAdminAccessMap(authorizedResourceAccess)
    : authorizedResourceAccess || {};
  const overviewAccess = Object.fromEntries(
    (Object.keys(overviewResources) as OverviewEntity[]).map((entity) => [
      `${overviewResources[entity]}-overview`,
      availableOverviewViews(entity, source).length > 0,
    ]),
  );

  const performanceGranted = (action: string) =>
    currentTenantIsAdmin ||
    source[`performance:${action}`] ||
    source['performance:*'] ||
    source['*:*'];
  const performanceView = ['view-self', 'view-team', 'view-all'].some(
    performanceGranted,
  );
  const performanceAccess = Object.fromEntries(
    ['overview', 'targets', 'entries', 'splits', 'team', 'reconciliation'].map(
      (page) => [
        `performance-${page}`,
        performanceView &&
          (page !== 'reconciliation' || Boolean(currentTenantIsAdmin)),
      ],
    ),
  );
  if (currentTenantIsAdmin) {
    return {
      ...buildAdminAccessMap(authorizedResourceAccess),
      ...overviewAccess,
      ...performanceAccess,
      modelSettings: true,
      'business-trips:manage-close-reasons': true,
    };
  }

  return {
    ...(authorizedResourceAccess || {}),
    ...overviewAccess,
    ...performanceAccess,
    modelSettings: false,
  };
}
